import React, { memo, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { API } from '@service';
import { Tooltip } from 'antd';
import { Loading } from '@components';
import EditableTable from './editableTable';
import EditButton from './editButton';
import * as Funcs from './sgaFunc';
// import SgaDropdown from './sgaDropdown';
import SgaChart from './sgaChart';

// import '@style/modules/analysis/sga.scss';
import '../../../../styles/modules/analysis/sga.scss';

const {
  SgaProto,
  isEmptyValue,
  formatModifiedRecordToSend,
  buildEditableRecords,
  formatSGADataToRecords,
  isToReplaceRecords,
  formatRecordToRender,
} = Funcs;

const SGA = () => {
  const { projectId, forceUpdateIds } = useSelector((state) => ({
    projectId: state.common.currentId,
    forceUpdateIds: state.analysis.forceUpdateIds,
  }));

  const { getSGAData, postSGAData } = API;

  const [sgaTableData, setSgaTableData] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [highlightedColIndex, setHighlightedColIndex] = useState(3);
  const [compData, setCompData] = useState([]);

  const initialSGATableData = useRef();
  const EditableTableEl = useRef();
  const isInternal = JSON.parse(
    window.localStorage.userInfo || '{}',
  ).isInternal;

  const initSGAData = (sgaRaw = {}) => {
    let data;
    if (isToReplaceRecords(sgaRaw)) {
      const replacement = buildEditableRecords(sgaRaw.others);
      data = Array.prototype.concat(
        [],
        initialSGATableData.current.slice(0, 3),
        replacement,
      );
    } else {
      data = formatSGADataToRecords(sgaRaw);
    }
    initialSGATableData.current = data.map((e, i) =>
      Object.assign(e, { key: i }),
    );
    setSgaTableData(data);
  };

  const fetchSgaData = async () => {
    setIsLoading(true);
    try {
      const { defaults, others, competitors } = await getSGAData(projectId);
      initSGAData({ others, defaults, competitors });
      setCompData(competitors.map(formatRecordToRender));
      setIsLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  const postModifiedData = async (data) => {
    try {
      const replacement = await postSGAData(projectId, data);
      initSGAData({ others: replacement });
    } catch (e) {
      setSgaTableData([...initialSGATableData.current]);
      console.error(e);
    }
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const onCancel = () => {
    setSgaTableData([...initialSGATableData.current]);
  };

  const saveEdit = async () => {
    const modifiedData = EditableTableEl.current.retrieveData();
    const data = modifiedData.map(formatModifiedRecordToSend);
    setIsSaving(true);
    await postModifiedData(data);
    setIsSaving(false);
    toggleEdit();
  };

  useEffect(() => {
    fetchSgaData();
  }, [forceUpdateIds]);

  const onEachHeaderCell = (i) => () => ({
    onClick: () => setHighlightedColIndex(i),
  });

  const Renderers = {
    pct: (v) => {
      if (isEmptyValue(v)) {
        return '-';
      }
      if (v < 0) {
        return `(${(-v).toFixed(1)}%)`;
      }
      return `${v}%`;
    },
    mn: (v) => {
      if (isEmptyValue(v)) {
        return '-';
      }
      let formatedStr = String(v).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
      if (Number(v) < 0) {
        formatedStr = `(${formatedStr.substring(1, formatedStr.length)})`;
      }
      return (
        <Tooltip
          placement="topRight"
          title={formatedStr.length > 10 ? formatedStr : null}
        >
          <span> {formatedStr}</span>
        </Tooltip>
      );
    },
  };

  const addRenderToCol = (col) => {
    const newCol = Object.assign({}, col);
    if (SgaProto.pctFields.includes(col.dataIndex)) {
      newCol.render = Renderers.pct;
    } else if (SgaProto.mnFields.includes(col.dataIndex)) {
      newCol.render = Renderers.mn;
    }
    return newCol;
  };

  const genChartDataBySgaRecord = (field) => (record) =>
    Object.assign({}, { value: record[field], name: record.companyName });

  const addHeaderClickEvent = (c, i) => {
    if (i < 2) {
      return c;
    }
    return Object.assign(c, { onHeaderCell: onEachHeaderCell(i) });
  };

  const columns = [
    {
      title: <Tooltip title="Company">Company</Tooltip>,
      dataIndex: 'companyName',
      ellipsis: {
        showTitle: false,
      },
      render: (company) => (
        <Tooltip placement="topLeft" title={company}>
          {isEmptyValue(company) ? '-' : company}
        </Tooltip>
      ),
    },
    {
      title: (
        <Tooltip title="Revenue" placement="topRight">
          <span>REV($ M)</span>
        </Tooltip>
      ),
      isNumber: true,
      dataIndex: 'revenue',
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: (
        <Tooltip title="Selling, General & Administrative as % of Revenue">
          <span>SG&A%</span>
        </Tooltip>
      ),
      isNumber: true,
      dataIndex: 'sgaPct',
    },
    {
      title: (
        <Tooltip title="General & Administrative as % of Revenue">
          <span>G&A%</span>
        </Tooltip>
      ),
      isNumber: true,
      dataIndex: 'gaPct',
    },
    {
      title: (
        <Tooltip title="Sales & Marketing as % of Revenue">
          <span>S&M%</span>
        </Tooltip>
      ),
      isNumber: true,
      dataIndex: 'smPct',
    },
    {
      title: (
        <div style={{ width: '100%', height: '100%' }}>
          <Tooltip
            title="Research & Development as % of Revenue"
            placement="topRight"
          >
            <span>R&D%</span>
          </Tooltip>
        </div>
      ),
      isNumber: true,
      dataIndex: 'rdPct',
    },
  ]
    .map(addRenderToCol)
    .map(addHeaderClickEvent);

  const findFieldByIndex = (index) =>
    (columns.find((_, i) => i === index) || {}).dataIndex;

  console.log(
    'genChartDataBySgaRecord: ',
    sgaTableData.map(
      genChartDataBySgaRecord(findFieldByIndex(highlightedColIndex)),
    ),
  );
  const sgaContent = isLoading ? (
    <Loading className="sga-loading" />
  ) : (
    <div className="sga-content-wrapper">
      {isInternal && (
        <EditButton
          isEditing={isEditing}
          isLoading={isSaving}
          toggleEdit={toggleEdit}
          saveEdit={saveEdit}
          onCancel={onCancel}
        />
      )}

      <div className="sga-table-wrapper">
        <EditableTable
          // className={tableClassName}
          highlightedColIndex={highlightedColIndex}
          dataSource={sgaTableData}
          isEditing={isEditing}
          ref={EditableTableEl}
          columns={columns}
          compData={compData}
        />
      </div>
      {/* <SgaDropdown onSelect={(e) => setHighlightedColIndex(parseInt(e, 10))} /> */}
      <SgaChart
        chosenField={findFieldByIndex(highlightedColIndex)}
        chartDataSrc={sgaTableData.map(
          genChartDataBySgaRecord(findFieldByIndex(highlightedColIndex)),
        )}
      />
    </div>
  );

  return (
    <div className="analysis-right-wrapper">
      <div className="title">SG&A Expense</div>
      <div className="sga-wrapper">{sgaContent}</div>
    </div>
  );
};

export default memo(SGA);
